import { config as envConfig } from '../../env';

// Single source for the "free trial" phrasing — reused by checkout copy below
// and by the landing pricing footnote, so the wording never drifts.
export const trialShortNote = (days) => `${days}-day free trial`;

export const CHECKOUT_COPY = {
  backToWebsite: 'Back to website',
  title: 'Payment method',
  subtitle: 'Choose payment method to complete purchase',
  emailLabel: 'Your email address',
  emailPlaceholder: 'mail@mail.com',
  emailHint: 'We need this to set up your account for you',
  firstNameLabel: 'First name',
  lastNameLabel: 'Last name',
  cardNumberLabel: 'Card number',
  expiryLabel: 'Expiration date',
  cvcLabel: 'Security code',
  summaryTitle: 'Review your subscription',
  rowMonthly: 'Monthly cost',
  rowToPay: 'To pay now',
  submit: 'Complete purchase',
  processing: 'Processing…',
  brand: 'Traders Connect',
  login: 'Login',
  drawer: {
    closeAria: 'Close checkout'
  },
  qty: {
    decrease: 'Decrease',
    increase: 'Increase'
  },
  summary: {
    orderTitle: 'Order summary',
    previewTitle: 'Subscription preview',
    updating: 'Updating…',
    saveLabel: (amount) => `Save ${amount}`,
    savePercentSuffix: (n) => `· ${n}% off`,
    prorationNote: 'Total reflects prorated changes to your subscription. Add-ons may be invoiced separately.',
    envCount: (n) => `${n} env${n === 1 ? '' : 's'}`,
    accountCount: (n) => `${n} ${n === 1 ? 'account' : 'accounts'}`,
    dueTodayTitle: 'Due today',
    startingTitle: (date) => `Starting ${date}`,
    recurringTitle: 'Starting next cycle',
    subtotalLabel: 'Subtotal',
    discountLabel: 'Discount',
    // Extension line under the Discount row — says WHICH code produced the
    // saving and on what terms, so the number is attributable at a glance.
    discountAppliedNote: (code, terms) => (terms ? `${code} — ${terms}` : code),
    discountTerms: {
      once: 'this invoice only',
      repeating: 'this and upcoming invoices',
      forever: 'every invoice'
    },
    // A credit reverses seats bought under an earlier discount: Stripe refunds
    // them at the rate paid, then re-bills the new quantity at whatever this
    // invoice's discount is. With no code on this cart, that means the old
    // seats silently return to standard rate for the rest of the cycle.
    discountNotCarried:
      "Your earlier discount doesn't carry over. Seats you already have are re-billed at the standard rate for the rest of this cycle.",
    // The cancel offer the customer took instead of cancelling: billing-manager puts it on a new plan
    // (never on seats, never on a crypto payment). No code is ever shown for it.
    cancelOffer: {
      label: (percent) => `${percent} offer`,
      tag: 'One-time',
      waiting: (percent) => `Your ${percent} offer is waiting for a new plan or your next renewal. Adding seats doesn't use it.`,
      cardOnly: (percent) => `Your ${percent} offer applies to card payments only.`
    },
    discountCode: {
      prompt: 'Have a discount code?',
      placeholder: 'Enter code',
      inputAria: 'Discount code',
      apply: 'Apply',
      removeAria: 'Remove discount code',
      // Verification round-trip in flight (couponInfo status 'pending').
      checking: 'Checking code…',
      notRecognized: "We couldn't apply that code — check the spelling or try another.",
      // The code is real (server said so) but discounts nothing here: its
      // product/price allowlist misses every line being changed, or this
      // customer has already redeemed it. Either way "applied at payment"
      // would be a promise the invoice won't keep.
      notApplicable: "That code doesn't apply to anything in this purchase.",
      // A code rides every purchase op (create / add / modify); only a cart
      // with nothing to buy at all has nothing for it to apply to.
      noPurchases: 'Add something to the cart to use a discount code.',
      // Positive feedback when the discount ledger row can't render (no live
      // preview for first-time buyers; $0 trial invoices carry no discount).
      appliedAtPayment: (code) => `Code ${code} will be applied at payment.`,
      trialNoDiscount: "Discount codes don't apply to free-trial invoices.",
      // Mixed cart: the code discounts the paid lines, but the trialing one is
      // created without it and bills in full when the trial converts.
      trialItemsNotDiscounted: "Trial items aren't discounted — they bill at full price when the trial ends."
    },
    creditLabel: 'Credit applied',
    creditsSectionLabel: 'Credits',
    chargesSectionLabel: 'Charges (prorated)',
    prorationTag: 'prorated',
    previewError: "Couldn't load latest price. Retrying…",
    noChange: 'No changes to apply',
    finalNote: 'Final invoice may differ slightly (taxes, payment method)'
  },
  banner: {
    pastDueTitle: 'Subscription past due',
    pastDueBody: 'Renew to keep your accounts active. Adjust your plan below before continuing to payment.',
    pastDueCta: 'Pay now',
    scheduledCancelTitle: 'Subscription scheduled to cancel',
    scheduledCancelBody: (when) => `Ends ${when || 'soon'}. Reactivate from the subscription list or review your plan below.`,
    cancellationScheduledTitle: (date) => `Cancellation scheduled for ${date}`,
    cancellationScheduledBody: 'Changes below will update your plan. Subscription remains active until period end.',
    activeTitle: 'Active plan',
    activeBody: 'Update seats or add products below. Changes use prorated billing.'
  },
  stripeDisabled: 'Stripe disabled — set NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY in .env.local and restart dev server.',

  configureTitle: 'Configure subscription',
  configureSubtitle: 'Choose the subscription plan that works best for you',
  editSubscription: 'Edit subscription',
  productLabel: 'Product',
  billingCycleLabel: 'Billing cycle',
  numberOfAccountsLabel: 'Number of accounts',
  standardAccountsLabel: 'Standard accounts',
  premiumAccountsLabel: 'Premium accounts',
  futuresAccountsLabel: 'Futures accounts',
  // Copier is billed per account and the figures beside it are LINE TOTALS, so
  // the unit has to be named or "$30.00/mo" reads as a plan price. The cycle
  // suffix is passed in rather than baked in — the unit is not "/mo" on a
  // quarterly, 6-month or annual cart.
  perAccountSuffix: ' per account',
  perAccountHint: (unitWithSuffix, count) => `${unitWithSuffix} per account × ${count}`,
  // Advisory only — it must never disable checkout. Copying needs a source and
  // a destination, but the second account may be a Futures one, and an
  // Analyzer-only setup never copies and is fine on a single account.
  minCopierAccounts: {
    title: 'Copying needs 2 accounts',
    body: "You've selected 1 account. Trades copy from one account to another, so you'll need a second one for copying to run.",
    action: 'Add second account',
    // Asked at the moment of checkout, because the inline notice above is easy
    // to scroll past. It confirms and continues — a single copier account is
    // still a legitimate purchase, so this must never be a hard gate.
    confirmTitle: 'Continue with 1 account?',
    confirmBody:
      "Copying runs between two accounts — a source and a destination — so it won't run with just one. One account is fine if you're pairing it with a Futures subscription, or not copying at all.",
    confirmProceed: 'Continue with 1 account',
    confirmCancel: 'Go back'
  },
  trialShortNote,
  trialBadge: (days) => `${trialShortNote(days)} included · $0 due today`,
  totalLabel: 'Total',
  totalAccountsLabel: 'Total accounts',
  // A trialing line is charged nothing today; its price arrives when the trial
  // converts. Showing the price alone made the lines contradict the total on a
  // cart that mixes trial and paid items.
  freeTrialValue: (zero) => `${zero} today`,
  afterTrialLineLabel: (days) => (days > 0 ? `after the ${days}-day free trial` : 'after the free trial'),
  addItemProrationNote:
    'New items are added to your current billing cycle. Your first charge will be prorated for the remaining days; the full amount applies from the next renewal.',
  proceedToCheckout: 'Proceed to checkout',
  brandPanel: {
    eyebrow: 'Checkout',
    steps: [
      { num: '01', title: 'Configure', sub: 'Tier & cycle' },
      { num: '02', title: 'Payment', sub: 'Details & card' }
    ],
    trust: { ssl: 'Secured by Stripe', cancel: 'Cancel anytime' }
  },
  configure: {
    eyebrow: 'Step 01 — Configure',
    titleStart: 'Configure your',
    titleAccent: 'stack.',
    subtitle: 'Mix standard, premium and futures accounts. Switch billing cycles for instant savings.',
    sectionBilling: 'Billing cycle',
    sectionAccounts: 'Accounts',
    tiers: {
      standard: {
        name: 'Standard',
        description: 'Cross-platform copier · 9+ platforms · pay per account'
      },
      premium: {
        name: 'Premium',
        badge: 'Most popular',
        description: 'Ultra-low latency · HFT support · priority'
      },
      futures: {
        name: 'Futures',
        description: 'AI trade analysis · ROI · win rate · heatmaps'
      }
    },
    futuresPrefix: 'From ',
    futuresExtraPrefix: '(+',
    futuresExtraSuffix: ' each extra)',
    futuresModeLabel: 'Pricing',
    futuresModePayg: 'Pay as you go',
    futuresModeUnlimited: 'Unlimited',
    futuresModeUnlimitedNote: 'One flat price',
    futuresUnlimitedBadge: 'Unlimited',
    futuresUnlimitedSummary: 'Unlimited accounts',
    addLabel: 'Add',
    currentPlanBadge: 'Active',
    // Trial-or-buy choice (shown only when the cart is trial-eligible).
    trialChoiceLabel: 'How would you like to start?',
    trialOptionTrialTitle: (days) => `${days}-day free trial`,
    trialOptionTrialNote: '$0 due today',
    trialOptionTrialLimit: (n) => `Covers up to ${n} accounts`,
    trialOptionBuyTitle: 'Buy now',
    trialOptionBuyNote: 'Skip trial — pay today',
    cta: 'Proceed to checkout',
    catalogLoading: 'Loading pricing…',
    catalogError: 'Couldn’t load live pricing. Showing reference prices.'
  },
  // Rooms are bought and cancelled from the Rooms page; only the cart line is left here.
  signalManager: {
    title: 'Signal Manager',
    includedNote: (baseSeats) => `Includes 1 master + ${baseSeats} followers`
  },
  addons: {
    sectionLabel: 'Add-ons',
    needsAccounts: 'Add a Standard, Premium or Futures account to turn on Analyzer.',
    activeBadge: 'Active',
    addEnv: 'Add',
    addAnotherEnv: 'Add another environment',
    cycleSwitchAnnual: 'Annual · save 2 mo',
    cycleSwitchMonthly: 'Monthly',
    // Each dedicated environment is billed as its own subscription, so only one
    // new env can be added per checkout. Shown once a new env is staged.
    dedicatedOnePerCheckout: 'One environment per checkout. Complete this purchase, then add another.',
    // Shown while managing an existing DE sub — adding another must be its own
    // checkout (separate subscription/payment), so the add button is hidden.
    dedicatedManageNote: 'Adjust this environment here. To add another, purchase it in a separate checkout.',
    // A new DE is its own subscription (its own payment), so it can't be checked
    // out alongside other plan changes. Shown when the cart mixes the two.
    dedicatedSeparateCheckout:
      'A dedicated environment is billed as its own subscription, so it must be purchased on its own. Check out your other changes first, then add the environment separately.',
    removeEnv: (idx) => `Remove environment ${idx + 1}`,
    envLabel: (idx) => `Environment ${idx + 1}`,
    envAccountsSummary: (count) => `${count} ${count === 1 ? 'account' : 'accounts'}`,
    envUnlimitedAccounts: 'Unlimited accounts',
    perMonthSuffix: '/mo',
    // DE card header prices both kinds — the kind is picked per env below, so the
    // header states the catalog and must not swing with what's in the cart.
    envKindPriceLine: (kindLabel, price, detail) => `${kindLabel} ${price} per env · ${detail}`,
    envExtraAccountNote: (extra) => `+${extra} per extra account`,
    envUnlimitedNote: 'unlimited accounts',
    // DE pricing block on the integrations page — one card per env kind.
    pricingTable: {
      title: 'Pricing',
      note: 'Pick the environment type at checkout. It must match the platform your accounts trade on.',
      cfdAccounts: (included, extra) => `${included} included ${extra}`
    },
    envKindAria: (idx) => `Environment ${idx + 1} type`
  },
  cancel: {
    modal: {
      keep: 'Keep subscription',
      confirm: 'Confirm cancel',
      busy: 'Cancelling…'
    },
    tier: {
      buttonLabel: 'Cancel subscription',
      title: (productLabel) => (productLabel ? `Cancel ${productLabel} subscription?` : 'Cancel subscription?'),
      description: 'Your plan stays active until the end of the current billing period. No further charges will be made after that.',
      confirmLabel: 'Cancel at period end',
      success: 'Cancellation scheduled for the end of your billing period.',
      error: 'Could not cancel subscription.'
    },
    tierLine: {
      buttonLabel: 'Remove from plan',
      title: (productLabel) => (productLabel ? `Remove ${productLabel}?` : 'Remove product?'),
      description:
        'This removes the product from your subscription immediately. Other products on the same subscription are not affected. A prorated credit or charge is applied on your next invoice.',
      confirmLabel: 'Remove',
      success: 'Product removed from subscription.',
      error: 'Could not remove product.'
    },
    analyzer: {
      ariaLabel: 'Cancel Analyzer subscription',
      title: 'Cancel Analyzer subscription?',
      description:
        'Analyzer access stays active until the end of the current billing period. After that, the add-on is removed and no further charges are made.',
      confirmLabel: 'Cancel at period end',
      success: 'Analyzer cancellation scheduled for period end.',
      error: 'Could not cancel Analyzer.'
    },
    analyzerLine: {
      ariaLabel: 'Remove Analyzer from subscription',
      title: 'Remove Analyzer from your plan?',
      description:
        'This removes Analyzer from your subscription immediately. Other products on the same subscription are not affected. A prorated credit is applied on your next invoice.',
      confirmLabel: 'Remove Analyzer',
      success: 'Analyzer removed from subscription.',
      error: 'Could not remove Analyzer.'
    },
    mcp: {
      ariaLabel: 'Cancel AI Connect subscription',
      title: 'Cancel AI Connect subscription?',
      description:
        'AI Connect stays active until the end of the current billing period. After that, AI trading is disabled on every account and no further charges are made.',
      confirmLabel: 'Cancel at period end',
      success: 'AI Connect cancellation scheduled for period end.',
      error: 'Could not cancel AI Connect.'
    },
    mcpLine: {
      ariaLabel: 'Remove AI Connect from subscription',
      title: 'Remove AI Connect from your plan?',
      description:
        'This removes AI Connect from your subscription immediately and disables AI trading on every account. Other products on the same subscription are not affected. A prorated credit is applied on your next invoice.',
      confirmLabel: 'Remove AI Connect',
      success: 'AI Connect removed from subscription.',
      error: 'Could not remove AI Connect.'
    },
    eaHosting: {
      label: 'Cancel EA Hosting',
      ariaLabel: 'Cancel EA Hosting subscription',
      title: 'Cancel EA Hosting subscription?',
      description:
        "EA Hosting stays active until the end of the current billing period. Then your hosted bots are stopped. They and their settings aren't deleted: if you subscribe again, start them from the Fleet.",
      confirmLabel: 'Cancel at period end',
      success: 'EA Hosting cancellation scheduled for period end.',
      error: 'Could not cancel EA Hosting.'
    },
    // No `eaHostingLine` variant: EA Hosting is always its own subscription, so
    // it is never a removable line on someone else's plan — cancelling the
    // subscription is the only path.
    dedicated: {
      buttonLabel: 'Cancel environment',
      title: (envLabel) => (envLabel ? `Cancel ${envLabel}?` : 'Cancel dedicated environment?'),
      description:
        'The environment stays active until the end of the current billing period. After that, accounts on this environment move to the shared public proxy. Other dedicated environments are not affected.',
      confirmLabel: 'Cancel environment',
      success: 'Environment cancellation scheduled for period end.',
      error: 'Could not cancel environment.'
    },
    dedicatedLine: {
      buttonLabel: 'Remove environment',
      title: (envLabel) => (envLabel ? `Remove ${envLabel}?` : 'Remove dedicated environment?'),
      description:
        'This removes the environment from your subscription immediately. Accounts on it move to the shared public proxy. Other products on the same subscription are not affected. A prorated credit is applied on your next invoice.',
      confirmLabel: 'Remove environment',
      success: 'Environment removed from subscription.',
      error: 'Could not remove environment.'
    }
  },
  analyzerToggleAria: {
    enable: 'Enable Analyzer',
    disable: 'Disable Analyzer'
  },
  analyzerLocked: {
    title: (cycleLabel) => `Active on your ${cycleLabel} plan`,
    description: 'Analyzer covers all your accounts — one purchase is enough across every billing cycle.',
    descriptionWithEnd: (endDate) => `Current period ends ${endDate}. You can change cycle after that.`,
    blockedToast: 'Analyzer is already active on another billing cycle.'
  },
  configureModify: {
    eyebrow: 'Step 01 — Update plan',
    titleStart: 'Update your',
    titleAccent: 'subscription.',
    subtitle:
      'Change seats on your current plan or add another product line. Updates apply to your existing subscription when you continue.',
    sectionBilling: 'Billing cycle',
    sectionAccounts: 'Accounts',
    billingLockedNote: 'New lines use the same billing cycle as your current plan. To change cycle, use account settings in the app.',
    tiersNote:
      'Plans you already have are highlighted. Seat changes update that subscription; new tiers start a new subscription or add a line as applicable.',
    multiSubIntro:
      'You have more than one active subscription. Adjust seats per product below—each product maps to its own subscription. Cancel or reactivate individually.',
    duplicateSubWarning:
      'You have multiple subscriptions for the same product. Seat totals are combined below; contact support if amounts look wrong.',
    singleSubIntro:
      'Use the seat controls below to update this subscription. Your total will reflect any prorated changes before you confirm.',
    currentPlanBadge: 'Active',
    dedicatedNote: 'Dedicated environment requests are submitted with checkout. Provisioning may take a short time after payment.',
    analyzerNote: 'Analyzer is billed as an add-on on your subscription when you continue to payment.',
    cta: 'Review changes',
    catalogLoading: 'Loading pricing…',
    catalogError: 'Couldn’t load live pricing. Showing reference prices.'
  },
  payment: {
    eyebrow: 'Step 02 — Payment',
    titleStart: 'Complete your',
    titleAccent: 'checkout.',
    subtitle: 'Your card is processed by Stripe with bank-grade encryption. We never see your full card details.',
    back: 'Edit subscription',
    sectionDetails: 'Your details',
    sectionCard: 'Card details',
    editDetails: 'Edit details',
    secureBadge: 'Verified by Cloudflare · Your information is encrypted end-to-end',
    ctaContinue: 'Continue to payment',
    disclaimerPre: 'By providing your card information, you authorize ',
    disclaimerCompany: 'Traders Connect App Limited',
    disclaimerPost: ' to charge your card for future payments in accordance with their terms. Cancel anytime from your dashboard.',
    ctaPayPrefix: 'Complete purchase',
    ctaProcessing: 'Processing…',
    secureFooter: 'Verified by Cloudflare · PCI DSS Level 1',
    successMessage: 'Subscription updated',
    errorFallback: 'Checkout failed',
    // Shown when the resolved plan carries no actionable change. Never let this
    // case fall through to successMessage: nothing was purchased.
    noChanges:
      'Nothing to purchase — your selection matches your current subscription. Close and reopen checkout, then pick your changes again.',
    cryptoErrorFallback: 'Crypto checkout failed',
    card: {
      loading: 'Loading card form…',
      unavailable: 'Card form unavailable.',
      initFailed: 'Failed to initialize card form',
      setupFailed: 'Card setup failed',
      setupIncomplete: 'Card setup did not succeed.',
      confirmFailed: 'Payment confirmation failed',
      savedTitle: 'Paying with saved card',
      savedLabel: (brand, last4) => `${brand || 'Card'} •••• ${last4 || ''}`.trim(),
      useDifferent: 'Use a different card',
      useSaved: 'Use saved card instead',
      savedLoading: 'Checking saved cards…'
    },
    tabs: {
      card: 'Card',
      crypto: 'Crypto',
      ariaLabel: 'Payment method'
    },
    crypto: {
      checking: 'Checking crypto payment status…',
      pendingTitle: 'Crypto payment pending',
      pendingDesc: (status) =>
        `A crypto purchase is in progress (${status || 'awaiting confirmation'}). Complete it before starting a new one.`,
      openPending: 'Open pending purchase',
      title: 'Pay with crypto',
      desc: "You'll be redirected to a hosted crypto checkout. Your subscription updates once the payment confirms on chain.",
      // Shown in place of the card disclaimer on the crypto tab — crypto is a
      // one-time payment (no stored card, no auto-renewal).
      disclaimer:
        "Crypto payments are one-time and processed by our crypto provider — they fund the current billing period and won't auto-renew. Add credit with crypto before it ends to keep your subscription running. Cancel anytime from your dashboard.",
      itemsHeading: "You'll be charged for",
      itemsNote: 'Each line becomes its own subscription that ends at the period close — re-purchase with crypto to extend.',
      itemLineLabel: (name, qty) => (qty > 1 ? `${name} × ${qty}` : name),
      itemUnknownProduct: 'Subscription item',
      cancelButton: 'Cancel pending purchase',
      cancelDialog: {
        title: 'Cancel this crypto payment?',
        body: 'If you have not paid on the linked checkout page yet, this clears the pending state so you can start over. If you have already sent crypto, do NOT cancel — wait for the payment to confirm on chain.',
        confirm: 'Yes, cancel it',
        dismiss: 'Keep waiting'
      },
      errors: {
        alreadyPending: 'A crypto payment is already pending.',
        noChanges: 'No pending changes to charge.',
        initFailed: 'Crypto checkout could not be initialized.',
        statusFailed: 'Failed to check crypto payment status',
        previewLoading: 'Pricing still loading — wait a moment and retry.',
        cancelFailed: 'Could not cancel the pending payment. It may already be confirming on chain.'
      }
    },
    overdue: {
      amountDueLabel: 'Amount due',
      balanceLabel: 'Account balance',
      // Stripe never puts credit on an invoice that is already open, so a card pays it in full.
      cardNoteTitle: "A card payment won't use your balance",
      cardKeepsBalance: (due, balance) =>
        `Your ${balance} balance came in after this invoice was issued. Your card is charged the full ${due}, and the ${balance} goes toward your next invoice.`,
      cardBalanceCovers: (balance) => `Your ${balance} balance came in after this invoice was issued, so it stays for your next invoice.`,
      cryptoCovers: (balance, due) => `Your ${balance} balance covers the ${due} due. Pay from balance settles it instantly.`,
      cryptoPart: (balance, due, rest) =>
        `Your ${balance} balance pays part of the ${due} due. Pay the other ${rest} with crypto; the invoice is settled once it confirms.`,
      cryptoFull: (due) => `Pay the ${due} due with crypto; the invoice is settled once it confirms.`,
      cryptoOwed: (owed, rest) => `Your account also owes ${owed}, so pay ${rest} with crypto; the invoice is settled once it confirms.`,
      payFromBalanceCta: (due) => `Pay ${due} from balance`,
      payWithCryptoCta: (due) => `Pay ${due} with crypto`,
      noLongerPayable: 'This invoice can no longer be paid. It was already settled or the subscription was cancelled.',
      amountChanged: (due) => `The amount due changed to ${due}. Check it and pay again.`
    }
  },
  success: {
    eyebrow: 'Confirmed',
    titleStart: "You're",
    titleAccent: 'in.',
    subtitle: 'Receipt is on its way to your inbox. Account provisioning now — head to the dashboard to set up your first copier.',
    subtitleUpdated: 'Your subscription changes are saved. Receipt is on its way to your inbox when a charge applies.',
    newNoticeTitle: 'One more step — set your password',
    newNoticeBody: 'We sent a password setup link to your email. Click it to finish creating your Traders Connect account.',
    statusLabel: 'Status',
    statusValue: 'Active',
    receiptLabel: 'Receipt',
    receiptValue: 'Sent to your email',
    cta: 'Go to dashboard',
    backLink: 'Back to website'
  }
};

export const PRODUCTS = {
  standard: 'standard',
  premium: 'premium',
  combo: 'combo',
  futures: 'futures'
};

/** Stripe price nickname prefixes for add-ons (see ListPrices). */
export const ADDON_PRODUCTS = {
  analyzer: 'analyzer',
  dedicated: 'dedicated',
  mcp: 'mcp',
  // Futures DE: same Stripe product as CFD DE, different price. Internal key (not
  // a Stripe product id) so parseNickname routes `futures_dedicated_env_*` here.
  dedicatedFutures: 'dedicatedFutures'
};

// Standalone product (hosted expert advisors, sold as "EA Hosting", surfaced in
// the app as EA Connect). NOT an add-on: it is bought and managed on its own
// subscription, needs no copier plan underneath it, and its quantity is bot
// SLOTS rather than trading accounts. Kept out of PRODUCTS/ADDON_PRODUCTS —
// like SIGNAL_MANAGER_PRODUCT — so it never leaks into trading-plan or add-on
// loops (seat maths, add-on cards, add-on entitlement flags).
export const EA_HOSTING_PRODUCT = 'eaHosting';

// Smallest quantity the checkout will send. EA Hosting is priced flat per bot
// slot with no bundled minimum, so this is simply 1 — you buy one bot, you pay
// for one bot. Kept as a named constant because the stepper, the clamp and the
// default selection all need the same floor.
export const EA_HOSTING_MIN_SLOTS = 1;

// Most bot slots an EA Hosting free trial covers: the trial is for trying one
// bot. More slots are paid from day one, and raising a trialing subscription
// above this ends the trial and bills now. Mirrors billing-manager's
// eaHostingTrialMaxSlots — BM refuses a trial above it.
export const EA_HOSTING_TRIAL_MAX_SLOTS = 1;

// Display + offline-fallback amounts for the EA Hosting plan. Live amounts come
// from listPrices (one flat per-unit price); these are the contract fallbacks,
// and they are deliberately NOT in ADDONS — nothing may render EA Hosting as
// an add-on card in the copier checkout.
export const EA_HOSTING_PLAN = {
  id: EA_HOSTING_PRODUCT,
  label: 'EA Hosting',
  slotPriceCentsMonthly: 1000,
  description: 'Run your MT4/MT5 expert advisors 24/7 on managed servers'
};

/**
 * Offline fallback for the EA Hosting monthly total at `slots`.
 * Flat per slot — no bundled base, no tier break, so the total is linear and
 * the per-bot price a buyer is quoted never changes with volume.
 */
export function eaHostingMonthlyCents(slots) {
  const n = Math.max(EA_HOSTING_MIN_SLOTS, Number(slots) || EA_HOSTING_MIN_SLOTS);
  return EA_HOSTING_PLAN.slotPriceCentsMonthly * n;
}

// Standalone org subscription (whitelabel, sold as "Signal Manager"). Kept out
// of PRODUCTS/ADDON_PRODUCTS so it never leaks into trading-plan loops.
export const SIGNAL_MANAGER_PRODUCT = 'signalManager';
export const SIGNAL_MANAGER_BASE_SEATS = 5;

/**
 * Stripe product/display name → product key, as ONE ordered table.
 *
 * Three files were each matching product names with their own copy of these
 * regexes — priceCatalog.productFromDisplayName, subscriptionLines
 * .subscriptionIncludesProduct and addonEntitlements — and they had drifted:
 * the last used `label.includes('mcp')`, so "webmcp" or "mcpx" read as the MCP
 * add-on. Everything now goes through this table, which also means adding a
 * product is one edit rather than three.
 *
 * ORDER IS SIGNIFICANT and productFromDisplayName walks it top-down:
 *   • premium before standard — "Accounts Premium" also contains "Accounts"
 *   • signal manager first — it is a standalone org SKU, not a trading plan
 * Patterns are matched against a lower-cased name, so they carry no /i.
 */
export const PRODUCT_NAME_PATTERNS = [
  [SIGNAL_MANAGER_PRODUCT, /white ?label|signal[ _]?manager/],
  [ADDON_PRODUCTS.mcp, /\bmcp\b|ai trading/],
  [EA_HOSTING_PRODUCT, /ea[ -_]?hosting|ea[ -_]?connect/],
  [ADDON_PRODUCTS.analyzer, /analyzer|analyser/],
  // Futures DE before plain dedicated/futures — its name carries both tokens.
  [ADDON_PRODUCTS.dedicatedFutures, /(?=.*dedic)(?=.*futures)/],
  [ADDON_PRODUCTS.dedicated, /dedic/],
  [PRODUCTS.futures, /futures/],
  [PRODUCTS.premium, /premium/],
  [PRODUCTS.standard, /accounts|\bstandard\b/]
];

/**
 * Does `name` denote `product`? Single source of truth for every
 * "is this subscription/label the X product?" check.
 *
 * @param {string} product a PRODUCTS / ADDON_PRODUCTS / SIGNAL_MANAGER_PRODUCT key
 * @param {string} name a Stripe product name, display name or overview label
 */
export function productNameMatches(product, name) {
  const n = String(name || '').toLowerCase();
  if (!n) return false;
  const entry = PRODUCT_NAME_PATTERNS.find(([key]) => key === product);
  return !!entry && entry[1].test(n);
}

export const PRODUCT_OPTIONS = [
  { id: PRODUCTS.standard, label: 'Standard' },
  { id: PRODUCTS.premium, label: 'Premium' },
  { id: PRODUCTS.combo, label: 'Standard + Premium' },
  { id: PRODUCTS.futures, label: 'Futures' }
];

export const CHECKOUT_STEPS = {
  configure: 'configure',
  payment: 'payment'
};

export const ADDONS = {
  analyzer: {
    id: 'analyzer',
    label: 'Analyzer',
    priceCentsMonthly: 2999,
    description: 'AI trade analysis · ROI · win rate · heatmaps'
  },
  dedicated: {
    id: 'dedicated',
    label: 'Dedicated Environment',
    // Each environment includes 1 account at the base price. Each extra
    // account in that same environment adds the per-extra-account fee.
    basePriceCentsMonthly: 3000,
    extraAccountCentsMonthly: 1000,
    description: 'Your own IP & hardware, for CFD or futures accounts'
  },
  // AI Trading (MCP) — flat standalone add-on. Live amounts come from
  // listPrices; these cents are the offline contract fallback. Only monthly +
  // annual are offered for now.
  mcp: {
    id: 'mcp',
    label: 'AI Trading (MCP)',
    priceCentsByCycle: {
      monthly: 5000,
      annual: 50000
    },
    priceCentsMonthly: 5000,
    description: 'AI places every order through the server-side policy gate'
  },
  // Flat per-env price lives in DEDICATED_FUTURES_FLAT_CENTS (needs BILLING_CYCLES).
  dedicatedFutures: {
    id: 'dedicatedFutures',
    label: 'Dedicated Environment',
    description: 'Your own IP & hardware for futures accounts'
  }
  // EA Hosting is NOT here on purpose — it is a standalone product, not an
  // add-on. See EA_HOSTING_PLAN above.
};

export function dedicatedEnvMonthlyCents(accts) {
  const n = Math.max(1, Number(accts) || 1);
  return ADDONS.dedicated.basePriceCentsMonthly + ADDONS.dedicated.extraAccountCentsMonthly * (n - 1);
}

export const BILLING_CYCLES = {
  monthly: 'monthly',
  quarterly: 'quarterly',
  semiAnnual: 'semiAnnual',
  annual: 'annual'
};

// Cycles on which Dedicated Environment is purchasable. Stripe catalog only
// has DE prices for monthly + annual; quarterly + semiAnnual variants don't
// exist, so the checkout drawer must not surface DE on those tabs.
export const DE_SUPPORTED_CYCLES = new Set([BILLING_CYCLES.monthly, BILLING_CYCLES.annual]);

// DE kind, chosen per-env: CFD (per-account tiered) or Futures (flat per env).
// Selects which Stripe add-on product resolves the live catalog price.
export const DEDICATED_KINDS = { cfd: 'cfd', futures: 'futures' };
export const DEFAULT_DEDICATED_KIND = DEDICATED_KINDS.cfd;
export const DEDICATED_KIND_OPTIONS = [
  { id: DEDICATED_KINDS.cfd, label: 'CFD' },
  { id: DEDICATED_KINDS.futures, label: 'Futures' }
];
export const DEDICATED_KIND_LABELS = new Map(DEDICATED_KIND_OPTIONS.map((o) => [o.id, o.label]));

export const DEDICATED_KIND_PRODUCT = {
  [DEDICATED_KINDS.cfd]: ADDON_PRODUCTS.dedicated,
  [DEDICATED_KINDS.futures]: ADDON_PRODUCTS.dedicatedFutures
};

// Both DE product keys — iterate this anywhere DE prices are collected so
// futures DE is never treated as non-DE activity.
export const DEDICATED_PRODUCTS = [ADDON_PRODUCTS.dedicated, ADDON_PRODUCTS.dedicatedFutures];

// Flat Futures-DE display price per env, by cycle (mirrors the Stripe prices).
// Authoritative charge still comes from the live catalog.
export const DEDICATED_FUTURES_FLAT_CENTS = {
  [BILLING_CYCLES.monthly]: 2500,
  [BILLING_CYCLES.annual]: 25000
};

export function normalizeDedicatedKind(kind) {
  return kind === DEDICATED_KINDS.futures ? DEDICATED_KINDS.futures : DEDICATED_KINDS.cfd;
}

export function isFuturesDedicatedKind(kind) {
  return normalizeDedicatedKind(kind) === DEDICATED_KINDS.futures;
}

// Futures envs are flat/unlimited, so account counts only mean something once at
// least one env is CFD.
export function allEnvsAreFutures(envs, kinds) {
  const list = Array.isArray(envs) ? envs : [];
  return list.length > 0 && list.every((_v, i) => isFuturesDedicatedKind(kinds?.[i]));
}

export function dedicatedProductForKind(kind) {
  return DEDICATED_KIND_PRODUCT[normalizeDedicatedKind(kind)];
}

export function dedicatedFuturesFlatCents(cycle) {
  return DEDICATED_FUTURES_FLAT_CENTS[cycle] ?? DEDICATED_FUTURES_FLAT_CENTS[BILLING_CYCLES.monthly];
}

// Per-env display cents: CFD scales per account × paidMonths; Futures is flat.
export function dedicatedEnvCyclePriceCents(kind, accounts, cycle, paidMonths = 1) {
  if (isFuturesDedicatedKind(kind)) {
    return dedicatedFuturesFlatCents(cycle);
  }
  return dedicatedEnvMonthlyCents(accounts) * (Number(paidMonths) || 1);
}

// Monthly-equivalent list price, for the crossed-out "would-be" savings only.
export function dedicatedEnvMonthlyEquivCents(kind, accounts) {
  if (isFuturesDedicatedKind(kind)) {
    return DEDICATED_FUTURES_FLAT_CENTS[BILLING_CYCLES.monthly];
  }
  return dedicatedEnvMonthlyCents(accounts);
}

// Sum display cents across DE envs (kind-aware). skipOwned excludes envs already
// backed by a subscription. `arrays` is the extractDedicatedArrays shape.
export function sumDedicatedEnvCents(arrays, cycle, paidMonths = 1, { skipOwned = false } = {}) {
  const envs = Array.isArray(arrays?.envs) ? arrays.envs : [];
  const kinds = Array.isArray(arrays?.kinds) ? arrays.kinds : [];
  const subIds = Array.isArray(arrays?.subIds) ? arrays.subIds : [];
  return envs.reduce((sum, accts, i) => {
    if (skipOwned && subIds[i]) return sum;
    return sum + dedicatedEnvCyclePriceCents(kinds[i], accts, cycle, paidMonths);
  }, 0);
}

// Stripe-side `productName` strings as returned in subscription protos. Keep
// in sync with billing-manager Stripe product config (values.yaml). Used by
// the table render to map Stripe names to user-facing labels.
export const STRIPE_PRODUCT_NAMES = {
  standard: 'Accounts',
  premium: 'Accounts Premium',
  futures: 'Futures',
  analyzer: 'Analyzer',
  dedicated: 'Dedicated Environment',
  signalManager: 'Signal Manager',
  mcp: 'AI Trading (MCP)',
  eaHosting: 'EA Hosting'
};

// User-facing labels keyed by Stripe productName. `short` is used in
// space-constrained spots like subtitle enumerations of bundle line items.
export const PRODUCT_LABELS = {
  [STRIPE_PRODUCT_NAMES.standard]: { full: 'Standard', short: 'Standard' },
  [STRIPE_PRODUCT_NAMES.premium]: { full: 'Premium', short: 'Premium' },
  [STRIPE_PRODUCT_NAMES.futures]: { full: 'Futures', short: 'Futures' },
  [STRIPE_PRODUCT_NAMES.analyzer]: { full: 'Analyzer', short: 'Analyzer' },
  [STRIPE_PRODUCT_NAMES.dedicated]: { full: 'Dedicated Environment', short: 'DE' },
  [STRIPE_PRODUCT_NAMES.signalManager]: { full: 'Signal Manager', short: 'SM' },
  // Legacy Stripe product name — old subs may still carry it.
  Whitelabel: { full: 'Signal Manager', short: 'SM' },
  [STRIPE_PRODUCT_NAMES.mcp]: { full: 'AI Trading', short: 'MCP' },
  [STRIPE_PRODUCT_NAMES.eaHosting]: { full: 'EA Hosting', short: 'EA' }
};

export const PRODUCT_LABEL_FALLBACK = 'Subscription';

/**
 * Map a Stripe `productName` to a display label.
 * @param {string} productName
 * @param {"full"|"short"} [variant="full"]
 * @returns {string}
 */
export function productLabel(productName, variant = 'full') {
  const entry = PRODUCT_LABELS[productName];
  if (entry) return entry[variant] ?? entry.full;
  return productName || PRODUCT_LABEL_FALLBACK;
}

// Copy strings used by the subscriptions table row subtitle. Centralized so
// translations or rewording happen in one place.
export const TABLE_SUBTITLE_COPY = {
  subscription: 'subscription',
  accountsSuffix: 'accounts',
  envPending: 'pending',
  lineJoiner: ', ',
  partSeparator: ' - ',
  bundleTitleJoiner: ' + '
};

// Webhook landing race buffers used by the table refresh after checkout.
// Stripe webhook → BM persistence is typically <1s; the longer buffer gives a
// safety margin for slower webhook delivery.
export const TABLE_REFRESH_FAST_MS = 50;
export const TABLE_REFRESH_DELAYED_MS = 1500;

export const CYCLE_CHIP = {
  [BILLING_CYCLES.monthly]: '',
  [BILLING_CYCLES.quarterly]: '',
  [BILLING_CYCLES.semiAnnual]: 'Save 1 month',
  [BILLING_CYCLES.annual]: 'Save 2 months'
};

// Inline price suffixes used inside checkout tier cards and totals.
// `/mo` and `/yr` are industry-universal short forms; `/quarter` and
// `/6 months` are spelled out to avoid the awkward `qtr` / `6mo`
// abbreviations that don't match how high-end SaaS surfaces price.
export const CYCLE_SUFFIX = {
  [BILLING_CYCLES.monthly]: '/mo',
  [BILLING_CYCLES.quarterly]: '/qtr',
  [BILLING_CYCLES.semiAnnual]: '/6mo',
  [BILLING_CYCLES.annual]: '/yr'
};

export const CYCLE_LABEL = {
  [BILLING_CYCLES.monthly]: 'month',
  [BILLING_CYCLES.quarterly]: 'quarter',
  [BILLING_CYCLES.semiAnnual]: '6 months',
  [BILLING_CYCLES.annual]: 'year'
};

/**
 * Human-readable "per <cadence>" phrase from a Stripe recurring interval +
 * intervalCount, e.g. month/3 → "per quarter".
 *
 * Lives here beside CYCLE_LABEL, whose vocabulary it reuses, rather than in
 * pbAdapter — that file's job is normalising jspb shapes at the proto boundary,
 * not producing display copy.
 */
export function intervalPriceLabel(interval, intervalCount = 1) {
  const ic = Number(intervalCount) || 1;
  if (interval === 'year') return `per ${CYCLE_LABEL[BILLING_CYCLES.annual]}`;
  if (interval === 'month') {
    if (ic === 3) return `per ${CYCLE_LABEL[BILLING_CYCLES.quarterly]}`;
    if (ic === 6) return `per ${CYCLE_LABEL[BILLING_CYCLES.semiAnnual]}`;
    if (ic >= 12) return `per ${CYCLE_LABEL[BILLING_CYCLES.annual]}`;
  }
  return `per ${CYCLE_LABEL[BILLING_CYCLES.monthly]}`;
}

export const BILLING_CYCLE_OPTIONS = [
  { id: BILLING_CYCLES.monthly, label: 'Monthly' },
  { id: BILLING_CYCLES.quarterly, label: 'Quarterly' },
  { id: BILLING_CYCLES.semiAnnual, label: 'Semi-annually' },
  { id: BILLING_CYCLES.annual, label: 'Annual' }
];

// Map<cycleId, label> — O(1) lookup. Prefer over BILLING_CYCLE_OPTIONS.find(...).
export const BILLING_CYCLE_LABELS = new Map(BILLING_CYCLE_OPTIONS.map((o) => [o.id, o.label]));

// Hardcoded landing/display pricing. Authoritative checkout pricing comes
// from GET /public/api/v1/billing/prices at runtime.

// Per-cycle futures display pricing — mirrors the graduated futures tiers from
// GET /billing/prices. Cycles are bespoke, not flat monthly × months.
const FUTURES_BY_CYCLE = {
  [BILLING_CYCLES.monthly]: { firstCents: 2000, extraCents: 300 },
  [BILLING_CYCLES.quarterly]: { firstCents: 5000, extraCents: 600 },
  [BILLING_CYCLES.semiAnnual]: { firstCents: 10000, extraCents: 1000 },
  [BILLING_CYCLES.annual]: { firstCents: 20000, extraCents: 2000 }
};

export const CHECKOUT_PRICING = {
  currency: 'usd',
  copier: {
    standardPerAccountCents: 1000,
    premiumPerAccountCents: 1500
  },
  futures: {
    firstAccountMonthlyCents: FUTURES_BY_CYCLE[BILLING_CYCLES.monthly].firstCents,
    extraAccountMonthlyCents: FUTURES_BY_CYCLE[BILLING_CYCLES.monthly].extraCents,
    trialDays: 10,
    byCycle: FUTURES_BY_CYCLE,
    // Accounts shipped in the tiered base fee. Fallback only — the live number
    // comes from the catalog price's first tier (`tiers[0].up_to`); used when
    // the catalog hasn't loaded yet to seed/floor the tiered count.
    tieredIncludedAccounts: 20
  },
  cycleMultipliers: {
    [BILLING_CYCLES.monthly]: 1,
    [BILLING_CYCLES.quarterly]: 3,
    [BILLING_CYCLES.semiAnnual]: 6,
    [BILLING_CYCLES.annual]: 12
  },
  cycleMonthsFree: {
    [BILLING_CYCLES.monthly]: 0,
    [BILLING_CYCLES.quarterly]: 0,
    [BILLING_CYCLES.semiAnnual]: 1,
    [BILLING_CYCLES.annual]: 2
  }
};

// Selection flags that route Payment to a create-only, SINGLE-product plan
// (PaymentStep branches on each of these before the full cart diff). A stale one
// silently drops every other change the user stages, so they must all be cleared
// whenever the buyer returns to Configure. Add any new standalone flag here and
// clearStandaloneFlags() picks it up automatically — the alternative is a
// hand-maintained list in useCheckoutCompat that quietly misses the new flag.
export const STANDALONE_SELECTION_FLAGS = ['signalManagerSelected', 'mcpStandalone', 'eaHostingStandalone'];

/** Selection patch that turns every standalone intent off. */
export function clearStandaloneFlags() {
  return Object.fromEntries(STANDALONE_SELECTION_FLAGS.map((k) => [k, false]));
}

export const DEFAULT_SELECTION = {
  product: PRODUCTS.combo,
  billingCycle: BILLING_CYCLES.monthly,
  // Staged by the Signal Manager card's Purchase click; routes Payment to the standalone plan.
  signalManagerSelected: false,
  // Staged by DirectBuyMcpModal; routes Payment to a standalone AI Connect (MCP)
  // subscription rather than adding an MCP line to the buyer's base plan. Keeps
  // the Stripe coupon scoped to MCP alone, which is what allows discount codes
  // on this flow. Distinct from `mcpEnabled`, which is the add-on toggle used by
  // the full subscription drawer.
  mcpStandalone: false,
  // EA Hosting standalone flag + staged slot count. Staged by the EA Connect
  // paywall/manage modal; routes Payment to a create-only EA Hosting plan (new
  // buyer) or a quantity modify on the owned EA Hosting sub (slot change).
  eaHostingStandalone: false,
  eaHostingSlots: EA_HOSTING_MIN_SLOTS,
  standardCount: 0,
  premiumCount: 0,
  futuresCount: 0,
  analyzerEnabled: false,
  // Each entry = account count for a dedicated env (min 1). Casts pin element
  // types so `Selection = typeof DEFAULT_SELECTION` isn't inferred as never[].
  dedicated: /** @type {number[]} */ ([]),
  // Parallel to `dedicated`: subscriptionId per env if backed by an
  // existing DE sub, else null (user-added env, not yet purchased).
  dedicatedSubIds: /** @type {(string | null)[]} */ ([]),
  // Parallel to `dedicated`: Stripe subscription_item_id per env when the env
  // is a line item inside a multi-item bundle sub. Null for legacy solo DE
  // subs (whole sub IS the env) or for unsaved env entries.
  dedicatedSubItemIds: /** @type {(string | null)[]} */ ([]),
  // Parallel to `dedicated`: BE-assigned nickname per env if backed by
  // a DE sub, else null. Falls back to "Environment N" in UI when null.
  dedicatedNicknames: /** @type {(string | null)[]} */ ([]),
  // Parallel to `dedicated`: total line count on the env's parent sub.
  // Cancel UX uses this to pick RemoveSubscriptionItem (>1) vs CancelSubscription
  // (sole line — BE rejects removing the last item). 0 for unsaved envs.
  dedicatedSubLineCounts: /** @type {number[]} */ ([]),
  // Parallel to `dedicated`: DE kind per env (DEDICATED_KINDS). Owned envs derive
  // it from their price nickname; new envs default to CFD.
  dedicatedKinds: /** @type {string[]} */ ([]),
  // When true the buyer explicitly opted out of the free trial on the
  // landing page (Futures "Buy now without free trial"). Suppresses
  // use_trial on checkout start even when all items are trial-eligible.
  skipTrial: false,
  // When true, futures uses the flat "unlimited" SKU (one $X/period for
  // unlimited futures accounts) instead of the tiered pay-as-you-go SKU.
  // Selection writers (UI toggle, post-purchase hydration from
  // `<product>_<cycle>_unlimited` nicknames) flip this; price-catalog +
  // checkout-item builders branch on it to pick the correct price_id.
  futuresUnlimited: false,
  // AI Trading (MCP) standalone add-on flag. When true the checkout item
  // builder emits the MCP price for `billingCycle`.
  mcpEnabled: false
};

export const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ?? '';

export const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY ?? '';

export const BILLING_API_BASE = process.env.REACT_APP_API_URL ?? '';

// Base URL of the unauthenticated billing REST API (billing-manager's
// /public/api/v1/billing/* routes). Envoy serves these on the SAME virtual
// host as the gRPC-web services, so this is simply the already-configured
// gateway (src/env per-environment config) — no separate env var. Only
// consumer today is discount-code validation (lib/billing/discountCode.js);
// if the host were ever empty the helper reports status 'unknown' and the
// checkout falls back to inferring code validity from the invoice preview.
export const PUBLIC_BILLING_API_BASE = envConfig.envoy_hostname ?? '';
